import React, { useState } from 'react';
import Header from '../components/Header';
import { useTasks } from '../context/TaskContext';
import TaskModal from '../components/TaskModal';

const Toggle = ({ on, onChange }) => (
  <button
    onClick={() => onChange(!on)}
    className={`w-10 h-5.5 flex items-center rounded-full transition-colors flex-shrink-0 ${on ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-700'}`}
    style={{ height: '22px', padding: '2px' }}
  >
    <span className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const Section = ({ title, children }) => (
  <div className="bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-2xl overflow-hidden mb-4">
    <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
    </div>
    <div className="divide-y divide-gray-50 dark:divide-gray-700/50">{children}</div>
  </div>
);

const Row = ({ label, desc, children }) => (
  <div className="flex items-center justify-between px-5 py-3.5">
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</p>
      {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
    </div>
    {children}
  </div>
);

const SettingsPage = () => {
  const { tasks, settings, updateSettings, resetTasks, clearAllTasks, dark, setDark } = useTasks();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  const completed = tasks.filter(t => t.status === 'Completed').length;
  const progress = tasks.length ? Math.round((completed / tasks.length) * 100) : 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header search={search} setSearch={setSearch} onAddTask={() => setModal({ type: 'add' })} pageTitle="Settings" />
      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 max-w-xl">

        <Section title="Appearance">
          <Row label="Dark Mode" desc="Use dark theme across the app">
            <Toggle on={dark} onChange={setDark} />
          </Row>
        </Section>

        <Section title="Notifications">
          <Row label="Enable Notifications" desc="Show deadline alerts and reminders">
            <Toggle on={settings.notifications} onChange={v => updateSettings({ notifications: v })} />
          </Row>
          <Row label="Deadline Alerts" desc="Highlight overdue and near-due tasks">
            <Toggle on={settings.deadlineAlerts} onChange={v => updateSettings({ deadlineAlerts: v })} />
          </Row>
        </Section>

        <Section title="Data & Storage">
          <Row label="Total Tasks" desc="Saved to your browser's localStorage">
            <span className="text-lg font-bold text-indigo-400">{tasks.length}</span>
          </Row>
          <Row label="Completion Rate" desc="Percentage of tasks completed">
            <span className="text-lg font-bold text-emerald-400">{progress}%</span>
          </Row>
          <Row label="Reset to Defaults" desc="Restore the original sample tasks">
            <button
              onClick={() => { if (window.confirm('Reset to default tasks?')) resetTasks(); }}
              className="text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Reset
            </button>
          </Row>
          <Row label="Clear All Tasks" desc="Permanently delete all tasks">
            <button
              onClick={() => { if (window.confirm('Delete all tasks? This cannot be undone.')) clearAllTasks(); }}
              className="text-xs px-3 py-1.5 bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 border border-red-200 dark:border-red-500/30 rounded-lg transition-colors"
            >
              Clear All
            </button>
          </Row>
        </Section>

        <Section title="About">
          <Row label="App" desc="Smart Productivity Dashboard">
            <span className="text-xs text-gray-400 font-mono">v1.0.0</span>
          </Row>
          <Row label="Framework" desc="React 18 + Tailwind CSS + Recharts">
            <span className="text-xs text-gray-400 font-mono">React 18</span>
          </Row>
          <Row label="Storage" desc="All data persists in localStorage">
            <span className="text-xs text-green-400">Active</span>
          </Row>
        </Section>
      </div>
      {modal && <TaskModal modal={modal} onClose={() => setModal(null)} />}
    </div>
  );
};

export default SettingsPage;
